import { Directive, OnDestroy, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { effect } from '@angular/core';
import { DeviceDetectorService } from '../../services';

/**
 * Base directive that handles rendering of content based on a condition
 * using Angular's signal architecture
 */
@Directive()
abstract class DeviceConditionDirective implements OnDestroy {
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef<unknown>);
  protected readonly deviceDetector = inject(DeviceDetectorService);

  private rendered = false;
  private cleanup = effect(() => {
    const shouldShow = this.checkCondition();

    if (shouldShow && !this.rendered) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.rendered = true;
    } else if (!shouldShow && this.rendered) {
      this.viewContainer.clear();
      this.rendered = false;
    }
  });

  ngOnDestroy(): void {
    this.cleanup.destroy();
  }

  /**
   * Abstract method that defines the condition to check
   * @returns boolean indicating whether to show the content
   */
  protected abstract checkCondition(): boolean;
}

/**
 * Directive to show content only on mobile devices
 * Usage: *libIfMobile
 *
 * Example:
 * <div *libIfMobile>This content is visible only on mobile phones</div>
 */
@Directive({
  selector: '[libIfMobile]',
})
export class IfMobileDirective extends DeviceConditionDirective {
  protected checkCondition(): boolean {
    return this.deviceDetector.isMobile();
  }
}

/**
 * Directive to show content only on tablet devices
 * Usage: *libIfTablet
 *
 * Example:
 * <div *libIfTablet>This content is visible only on tablets</div>
 */
@Directive({
  selector: '[libIfTablet]',
})
export class IfTabletDirective extends DeviceConditionDirective {
  protected checkCondition(): boolean {
    return this.deviceDetector.isTablet();
  }
}

/**
 * Directive to show content only on desktop devices
 * Usage: *libIfDesktop
 *
 * Example:
 * <div *libIfDesktop>This content is visible only on desktops</div>
 */
@Directive({
  selector: '[libIfDesktop]',
})
export class IfDesktopDirective extends DeviceConditionDirective {
  protected checkCondition(): boolean {
    return this.deviceDetector.isDesktop();
  }
}

/**
 * Directive to show content only on non-mobile devices (tablets and desktops)
 * Usage: *libIfNotMobile
 *
 * Example:
 * <div *libIfNotMobile>This content is visible on tablets and desktops, but not on mobile phones</div>
 */
@Directive({
  selector: '[libIfNotMobile]',
})
export class IfNotMobileDirective extends DeviceConditionDirective {
  protected checkCondition(): boolean {
    return !this.deviceDetector.isMobile();
  }
}

/**
 * Directive to show content only on mobile OR tablet devices (touch devices)
 * Usage: *libIfTouchDevice
 *
 * Example:
 * <div *libIfTouchDevice>This content is visible on mobile and tablet devices</div>
 */
@Directive({
  selector: '[libIfTouchDevice]',
})
export class IfTouchDeviceDirective extends DeviceConditionDirective {
  protected checkCondition(): boolean {
    return this.deviceDetector.isMobile() || this.deviceDetector.isTablet();
  }
}
